import { Component } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RotateCcw, Home } from "lucide-react";
import { resolveBackendUrl } from "../lib/backendUrl";

// Global runtime-error guard. Catches render/lifecycle crashes anywhere in the
// app (simulators included) and shows a friendly recovery screen instead of a
// blank/dev error overlay.
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.error("[ErrorBoundary]", error, info?.componentStack);
    }
    this.report(error, info);
  }

  report(error, info) {
    try {
      const base = resolveBackendUrl();
      if (!base) return;
      // Auth flows via HttpOnly cookies (credentials: "include"); we no longer
      // read a JWT from localStorage on the browser (2026-07-21 audit).
      // If we're authenticated the backend CSRF middleware will require a
      // matching X-CSRF-Token header on this POST — echo the non-HttpOnly
      // cookie value so the crash-report POST is accepted.
      const csrf = (() => {
        try {
          const parts = (document.cookie || "").split(";");
          for (const raw of parts) {
            const [k, ...rest] = raw.trim().split("=");
            if (k === "csrf_token") return decodeURIComponent(rest.join("="));
          }
        } catch { /* ignore */ }
        return null;
      })();
      fetch(`${base}/api/errors`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(csrf ? { "X-CSRF-Token": csrf } : {}),
        },
        credentials: "include",
        body: JSON.stringify({
          message: error?.message || String(error),
          stack: error?.stack || "",
          component_stack: info?.componentStack || "",
          route: window.location.pathname + window.location.search,
          user_agent: navigator.userAgent,
        }),
      }).catch(() => {});
    } catch {
      /* never let reporting throw */
    }
  }

  handleReset = () => this.setState({ hasError: false, error: null });

  render() {
    if (!this.state.hasError) return this.props.children;
    const msg = this.state.error?.message || "An unexpected error occurred.";
    return (
      <div className="min-h-screen bg-[#F4F7F9] flex items-center justify-center px-6" data-testid="error-boundary">
        <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 shadow-lg p-8 text-center fade-up">
          <div className="w-14 h-14 rounded-2xl bg-rose-50 border border-rose-200 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-7 h-7 text-rose-500" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Something went wrong</h1>
          <p className="text-sm text-slate-600 mt-2">
            This screen hit an unexpected error. Your saved progress is safe — try
            again or head back to the simulator hub.
          </p>
          <p className="text-[11px] font-mono text-slate-400 mt-3 break-words bg-slate-50 rounded-md px-3 py-2 border border-slate-100" data-testid="error-boundary-message">
            {msg}
          </p>
          <div className="flex gap-2 mt-6">
            <Button onClick={this.handleReset} data-testid="error-boundary-retry"
              className="flex-1 bg-[#0047AB] hover:bg-[#003380] text-white">
              <RotateCcw className="w-4 h-4 mr-1.5" /> Try again
            </Button>
            <Button variant="outline" onClick={() => { window.location.href = "/"; }}
              data-testid="error-boundary-home" className="flex-1 text-slate-600">
              <Home className="w-4 h-4 mr-1.5" /> Simulator hub
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
